import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Grow, Paper, Popper, Theme, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { LocalDataTrack, TwilioError } from 'twilio-video';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';

import useIsUserActive from './useIsUserActive/useIsUserActive';
import useRoomState from '../../hooks/useRoomState/useRoomState';
import ToggleVideoEMDR from './ToggleVideoEMDR/ToggleVideoEMDR';
import CompositeAudioButton from '../Controls/CompositeAudioButton/CompositeAudioButton';
import CompositeVideoButton from '../Controls/CompositeVideoButton/CompositeVideoButton';
import CompositeBackgroundButton from '../Controls/CompositeBackgroundButton/CompositeBackgroundButton';
import { DeviceDialogStyle } from '../Controls/DeviceDialog/style';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import ChatButton from '../ChatButton/ChatButton';
import useRemoteDatatrack from '../../hooks/useDatatrackParticipant/useRemoteDatatrack';
import MediaControls from './MediaControls/MediaControls';
import useAppContext from '../../hooks/useAppContext/useAppContext';
import clsx from 'clsx';
import usePractitionerControlsContext from '../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import useMediaContext from '../../hooks/useMediaContextHook/useMediaContext';
import ErrorDialog from '../ErrorDialog/ErrorDialog';
import { ModalUI } from '../UI/Modal/Modal';
import { ControlsPractitionerStyle } from './style';
import MediaModal from './MediaModal/MediaModal';
import ReactPlayer from 'react-player/lazy';
import { MediaTypes } from '../../commands/practitioner/enums/MediaTypes';
import useUploadContext from '../../hooks/useUploadContext/useUploadContext';
import { getResponsiveSize } from '../../utils';
import { isValidMediaUrl } from '../../utils/mediaDetection';
import ViewSwapToggle from './ViewSwapToggle/ViewSwapToggle';

const useStyles = makeStyles<Theme>((theme) => ({
  overall: {
    display: 'grid',
    gridTemplateColumns: '1fr auto 1fr',
    alignItems: 'center',
    position: 'absolute',
    bottom: '1%',
    left: 0,
    width: '75%',
    padding: 0,
    columnGap: getResponsiveSize(16),
    zIndex: 3,
  },
  overallInCall: {
    gridTemplateColumns: '2fr auto 1fr',
    padding: `0 0 0 ${getResponsiveSize(30)}`,
    width: '70%',
  },
  leftGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: getResponsiveSize(14),
    justifySelf: 'flex-start',
    transition: 'opacity 1.2s, transform 1.2s, visibility 0s 1.2s',
    visibility: 'visible',
    '& .MuiFab-root': {
      margin: '0 !important',
      width: '48px',
      height: '48px',
      minHeight: '48px',
    },
  },
  centerGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: getResponsiveSize(16),
    justifySelf: 'center',
    transition: 'opacity 1.2s, transform 1.2s, visibility 0s 1.2s',
    visibility: 'visible',
  },
  rightGroup: {
    display: 'flex',
    alignItems: 'center',
    justifySelf: 'flex-start',
    transition: 'opacity 1.2s, transform 1.2s, visibility 0s 1.2s',
    visibility: 'visible',
  },
  compositeSlot: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    '& .MuiSvgIcon-root': {
      fontSize: getResponsiveSize(20),
    },
    '& .MuiFab-root .MuiSvgIcon-root': {
      fontSize: '24px',
    },
  },
  endCallSlot: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    '& button': {
      cursor: 'pointer',
      border: 0,
      padding: 0,
      outline: 0,
      background: 'transparent',
      margin: 0,
      flexShrink: 0,
    },
    '& .call-button img': {
      width: '48px',
      height: '48px',
      minWidth: '48px',
      minHeight: '48px',
      flexShrink: 0,
      display: 'block',
    },
    '& .call-button:hover img, & .call-button:focus img': {
      opacity: 0.8,
    },
  },
}));

interface IControlPractitionerProps {
  currentState: any;
  toggleClientCallback?: () => void;
  setInCallButtonHostEl?: (el: HTMLDivElement | null) => void;
}

export default function ControlsPractitioner(props: IControlPractitionerProps) {
  const { hasDevice } = useAppContext();
  const {
    taskHasStarted,
    setOpenMediaModal,
    openMediaModal,
    practitionerMediaType,
    practitionerMediaURI,
    setIsMediaLoaded,
    status,
    isMediaLoaded,
    hasBeenSaved,
  } = usePractitionerControlsContext();
  const { isUploadTabOpen } = useUploadContext();
  const { setDiveIndex, setFirstDiveInd } = useMediaContext();
  const classes = useStyles();
  const { t } = useTranslation();
  const { localTracks } = useVideoContext();
  const isUserActive = useIsUserActive();
  const roomState = useRoomState();
  const remoteDatatrack = useRemoteDatatrack();
  const [isVideoActive, setVideoActive] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [generalError, setGeneralError] = useState<string>('');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const localDataTrack = localTracks.find((track) => track.kind === 'data') as LocalDataTrack;
  const isDisconnected = roomState === 'disconnected';
  const showControls = isUserActive || roomState === 'disconnected';
  const backgroundStyling = { backgroundColor: '#e1e0e1' };
  const { mediaState, errorMessage } = props.currentState;

  const { mediaModal, wrapper } = ControlsPractitionerStyle;

  useEffect(() => {
    if (isDisconnected) {
      setVideoActive(true);
    }
  }, [taskHasStarted, isDisconnected]);

  useEffect(() => {
    if (isUploadTabOpen) {
      setIsMediaLoaded(status);
      return;
    }

    if (!isValidMediaUrl(practitionerMediaURI)) {
      setIsMediaLoaded(false);
      return;
    }

    if (practitionerMediaType === MediaTypes.IMAGE) {
      setIsMediaLoaded(false);
      let cancelled = false;
      const img = new Image();
      img.src = practitionerMediaURI;
      img.onload = () => {
        if (!cancelled) setIsMediaLoaded(true);
      };
      img.onerror = () => {
        if (!cancelled) setIsMediaLoaded(false);
      };
      return () => {
        cancelled = true;
      };
    }

    setIsMediaLoaded(ReactPlayer.canPlay(practitionerMediaURI));
  }, [practitionerMediaURI, practitionerMediaType]);

  useEffect(() => {
    // Register for report on Video Toggle changes driven by client side actions
    if (remoteDatatrack) {
      remoteDatatrack.on('subscribed', (track) => {
        track.on('message', (msg: any) => {
          if (msg) {
            const { type, data } = JSON.parse(msg);
            if ((type as string) === 'VideoFrame Report') {
              setVideoActive(data);
            }
          }
        });
      });
    }
    return () => {
      if (remoteDatatrack) {
        remoteDatatrack.off('subscribed', (track) => {
          track.off('message', () => {});
        });
      }
    };
  }, [remoteDatatrack, setVideoActive]);

  const closeModal = useCallback(() => {
    setOpenMediaModal(false);
    setDiveIndex([]);
    setFirstDiveInd(true);
  }, []);

  useEffect(() => {
    setError(errorMessage);
  }, [errorMessage]);

  return (
    <Box sx={wrapper} className={clsx(classes.overall, { [classes.overallInCall]: !isDisconnected })}>
      {error ? <ErrorDialog dismissError={() => setError('')} error={new Error(error) as TwilioError} /> : null}
      {generalError ? (
        <ErrorDialog dismissError={() => setGeneralError('')} error={new Error(generalError) as TwilioError} />
      ) : null}
      <ModalUI modalSx={mediaModal} className={'media-modal'} open={openMediaModal} onCloseHandler={closeModal}>
        <MediaModal />
      </ModalUI>
      <div className={clsx(classes.leftGroup, { showControls })}>
        <div ref={props.setInCallButtonHostEl} className={classes.endCallSlot} />
        {!isDisconnected ? (
          <>
            <div className={classes.compositeSlot}>
              <CompositeAudioButton dialogWrapperSx={DeviceDialogStyle.deviceModalVideoArea} />
            </div>
            <div className={classes.compositeSlot}>
              <CompositeVideoButton dialogWrapperSx={DeviceDialogStyle.deviceModalVideoArea} />
            </div>
            <div className={classes.compositeSlot}>
              <CompositeBackgroundButton />
            </div>
            <div className={classes.compositeSlot}>
              <ChatButton
                t={t}
                anchorEl={anchorEl}
                setAnchorEl={setAnchorEl}
                localDatatrack={localDataTrack}
                remoteDatatrack={remoteDatatrack}
                isDisconnected={isDisconnected}
              />
            </div>
          </>
        ) : null}
      </div>
      <div className={clsx(classes.centerGroup, { showControls })}>
        {!hasDevice && !isDisconnected ? (
          <ToggleVideoEMDR
            t={t}
            isVideoEnabled={isVideoActive}
            setVideoEnabled={setVideoActive}
            remoteDatatrack={remoteDatatrack}
            localDatatrack={localDataTrack}
            isDisabled={taskHasStarted}
          />
        ) : null}
        <MediaControls modalIsOpened={openMediaModal} setModalIsOpened={setOpenMediaModal} mediaState={mediaState} />
      </div>
      {/*
        ViewSwapToggle lives in the right column (not the centre group) so the media
        controls stay centred under the call button. The grid's columnGap keeps it
        just to the right of the media controls.
      */}
      <div className={clsx(classes.rightGroup, { showControls })}>
        {!hasDevice ? (
          <ViewSwapToggle isCallActive={!isDisconnected} hasMedia={Boolean(isMediaLoaded && hasBeenSaved)} />
        ) : null}
      </div>
    </Box>
  );
}
